import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import { DataGrid } from '@mui/x-data-grid';
import { getMyReferrals } from '../services/internApi';
import { formatCurrency, formatDate } from '../utils/formatters';
import StatusChip from '../components/common/StatusChip';

export default function ReferralsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    getMyReferrals()
      .then(setData)
      .catch((err) => setError(err?.response?.data?.error || 'Failed to load referrals'))
      .finally(() => setLoading(false));
  }, []);

  const copyCode = () => {
    if (!data?.referral_code) return;
    navigator.clipboard.writeText(data.referral_code);
    setSnack({ open: true, message: 'Code copied to clipboard', severity: 'success' });
  };

  const copyLink = () => {
    if (!data?.share_url) return;
    navigator.clipboard.writeText(data.share_url);
    setSnack({ open: true, message: 'Share link copied', severity: 'success' });
  };

  const shareNative = async () => {
    if (!navigator.share) {
      copyLink();
      return;
    }
    try {
      await navigator.share({
        title: 'Get a Cyber Wise Corp assessment',
        text: `Book a cybersecurity assessment and use my code ${data?.referral_code} for a discount.`,
        url: data?.share_url,
      });
    } catch (e) {
      // user cancelled
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  const referrals = (data?.referrals || []).map((r, i) => ({ id: i, ...r }));

  const columns = [
    { field: 'client_name', headerName: 'Client', flex: 1 },
    { field: 'time_slot', headerName: 'Assessment Date', flex: 1, valueFormatter: (p) => formatDate(p.value) },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      renderCell: (p) => <StatusChip status={p.value} type="booking" />,
    },
    { field: 'bonus', headerName: 'Your Bonus', flex: 0.6, valueFormatter: (p) => formatCurrency(p.value) },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>Referrals</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Earn $10 for every assessment booked with your code.
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #1e3a8a, #7c3aed)', color: 'white' }}>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Your referral code</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, letterSpacing: 2, my: 1 }}>
              {data?.referral_code || '—'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
              Share this code. Every time someone books an assessment using it, you earn $10.
            </Typography>
            {data?.share_url && (
              <TextField
                value={data.share_url}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  sx: { backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } },
                }}
                sx={{ mb: 2 }}
              />
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={copyCode}
                sx={{ backgroundColor: 'white', color: '#1e3a8a', '&:hover': { backgroundColor: '#f0f0f0' } }}
              >
                Copy Code
              </Button>
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={copyLink}
                sx={{ backgroundColor: 'white', color: '#1e3a8a', '&:hover': { backgroundColor: '#f0f0f0' } }}
              >
                Copy Link
              </Button>
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={shareNative}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white' } }}
              >
                Share
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="body2" color="text.secondary">Total Referral Earnings</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#7c3aed', my: 1 }}>
              {formatCurrency(data?.total_earned || 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data?.count || 0} referral{(data?.count || 0) === 1 ? '' : 's'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Referral History</Typography>
        {referrals.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No referrals yet. Share your code to start earning.
          </Typography>
        ) : (
          <Box sx={{ height: 400 }}>
            <DataGrid
              rows={referrals}
              columns={columns}
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
